// MI-311 (MI-298 revision 7): resolve the user's "home" currency — the one the
// display-currency control opens on. Everything here is local: no IP lookup, no
// network call. Order of trust: the country the user gave us in the onboarding
// wizard, then the machine's time zone, then the browser language's region.
import { currencyForCountry } from './onboardingCountries';

// IANA zone → ISO 3166-1 alpha-2, limited to the countries COUNTRIES maps to a
// currency (anything else resolves to null and we fall through). Prefix rules
// below cover the zones that come in families (America/Argentina/*, US states…).
const TZ_COUNTRY = {
  'America/Bogota': 'CO',
  'America/Mexico_City': 'MX', 'America/Cancun': 'MX', 'America/Merida': 'MX',
  'America/Monterrey': 'MX', 'America/Chihuahua': 'MX', 'America/Hermosillo': 'MX',
  'America/Mazatlan': 'MX', 'America/Tijuana': 'MX',
  'America/Santiago': 'CL', 'Pacific/Easter': 'CL',
  'America/Lima': 'PE',
  'America/Sao_Paulo': 'BR', 'America/Bahia': 'BR', 'America/Fortaleza': 'BR',
  'America/Recife': 'BR', 'America/Manaus': 'BR', 'America/Belem': 'BR',
  'America/Cuiaba': 'BR', 'America/Campo_Grande': 'BR', 'America/Porto_Velho': 'BR',
  'America/Boa_Vista': 'BR', 'America/Rio_Branco': 'BR', 'America/Maceio': 'BR',
  'America/Araguaina': 'BR', 'America/Santarem': 'BR', 'America/Eirunepe': 'BR',
  'America/Noronha': 'BR',
  'America/Montevideo': 'UY',
  'America/La_Paz': 'BO',
  'America/Asuncion': 'PY',
  'America/Caracas': 'VE',
  'America/Guayaquil': 'EC', 'Pacific/Galapagos': 'EC',
  'America/New_York': 'US', 'America/Chicago': 'US', 'America/Denver': 'US',
  'America/Los_Angeles': 'US', 'America/Phoenix': 'US', 'America/Anchorage': 'US',
  'America/Detroit': 'US', 'America/Juneau': 'US', 'America/Adak': 'US',
  'America/Boise': 'US', 'America/Nome': 'US', 'America/Sitka': 'US',
  'America/Menominee': 'US', 'America/Metlakatla': 'US', 'America/Yakutat': 'US',
  'Pacific/Honolulu': 'US',
  'America/Toronto': 'CA', 'America/Vancouver': 'CA', 'America/Edmonton': 'CA',
  'America/Winnipeg': 'CA', 'America/Halifax': 'CA', 'America/St_Johns': 'CA',
  'America/Regina': 'CA', 'America/Moncton': 'CA', 'America/Whitehorse': 'CA',
  'America/Yellowknife': 'CA', 'America/Iqaluit': 'CA',
  'Europe/Madrid': 'ES', 'Atlantic/Canary': 'ES', 'Africa/Ceuta': 'ES',
  'Europe/London': 'GB',
};

const TZ_PREFIX_COUNTRY = [
  ['America/Argentina/', 'AR'],
  ['America/Indiana/', 'US'],
  ['America/Kentucky/', 'US'],
  ['America/North_Dakota/', 'US'],
];

export function countryFromTimeZone(zone) {
  if (!zone) return null;
  if (TZ_COUNTRY[zone]) return TZ_COUNTRY[zone];
  const prefix = TZ_PREFIX_COUNTRY.find(([p]) => zone.startsWith(p));
  return prefix ? prefix[1] : null;
}

// 'es-CO' → 'CO'. Locales without a region ('es', 'en') carry no country.
export function countryFromLocale(locale) {
  const region = String(locale || '').split('-')[1];
  return region && region.length === 2 ? region.toUpperCase() : null;
}

// The country we believe the user is in, best signal first.
export function detectCountry(preferredCountry) {
  if (preferredCountry) return preferredCountry;
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const fromTz = countryFromTimeZone(tz);
    if (fromTz) return fromTz;
  } catch (e) { /* Intl unavailable — fall through */ }
  const locales = (typeof navigator !== 'undefined' && (navigator.languages || [navigator.language])) || [];
  for (const loc of locales) {
    const fromLocale = countryFromLocale(loc);
    if (fromLocale) return fromLocale;
  }
  return null;
}

/**
 * The currency the display-currency control should open on, or null to keep the
 * per-currency view.
 *
 * Null on purpose when: no country resolves, its currency isn't one the user has
 * active, or fewer than two currencies are active — with a single currency,
 * converting changes nothing but would stamp exact figures as approximate.
 */
export function resolveHomeCurrency(preferredCountry, activeCurrencies) {
  const active = activeCurrencies || [];
  if (active.length < 2) return null;
  const currency = currencyForCountry(detectCountry(preferredCountry));
  return currency && active.includes(currency) ? currency : null;
}
